import { ApiError } from '@commissionsight/sdk';

/**
 * AI rate-schedule drafting — an app-only feature (not in the public SDK). The web
 * app calls the endpoints directly: draft from a pasted rate source, then review &
 * apply (a draft never becomes live config without this explicit apply).
 */
async function call<T>(
  baseUrl: string,
  token: string | null,
  path: string,
  init?: RequestInit,
): Promise<T> {
  const res = await fetch(`${baseUrl.replace(/\/$/, '')}${path}`, {
    ...init,
    headers: {
      'content-type': 'application/json',
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    },
  });
  const text = await res.text();
  const body = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const message =
      body && typeof body === 'object' && 'title' in body
        ? String((body as { title: unknown }).title)
        : res.statusText;
    throw new ApiError(res.status, message, body);
  }
  return body as T;
}

export type ProductLine = 'major_medical' | 'medicare' | 'ancillary';

export interface DraftedRate {
  draftId: string;
  productLine: ProductLine;
  config: Record<string, unknown>;
}

export function draftRates(
  baseUrl: string,
  token: string | null,
  input: { carrierId: string; productLine: ProductLine; source: string },
): Promise<DraftedRate> {
  return call(baseUrl, token, '/rate-schedules/ai-build', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function applyRateDraft(
  baseUrl: string,
  token: string | null,
  id: string,
): Promise<{ id: string; applied: boolean }> {
  return call(baseUrl, token, `/rate-schedules/drafts/${id}/apply`, { method: 'POST' });
}

export function rejectRateDraft(
  baseUrl: string,
  token: string | null,
  id: string,
): Promise<{ id: string; rejected: boolean }> {
  return call(baseUrl, token, `/rate-schedules/drafts/${id}/reject`, { method: 'POST' });
}
